import { useState } from 'react'
import {CheckCircle, AlertCircle, TrendingUp, Award, ExternalLink, ChevronDown, ChevronUp, Info} from 'lucide-react'

interface FormData {
  age: string
  gender: string
  lifestyle: string
  energyLevel: number
  concerns: string[]
  vitaminDLevel: string
}

interface Product {
  id: string
  name: string
  description: string
  icon: string
  rating: number
  benefits: string[]
  affiliateUrl: string
  reason: string
}

interface ResultsProps {
  formData: FormData
  onReset: () => void
}

function CalculatorResults({ formData, onReset }: ResultsProps) {
  const [showBreakdown, setShowBreakdown] = useState(false)

  // ============================================================
  // VITALITY SCORE ALGORITHM (documented for transparency)
  // ============================================================
  // Base Score: 40 points (neutral starting point)
  // 1. Energy Level Factor -> energyLevel * 3         (range: 3 to 30)
  // 2. Age Factor          -> tiered by age bracket    (range: 3 to 10)
  // 3. Lifestyle Factor    -> tiered by activity level (range: 5 to 15)
  // 4. Health Concerns     -> -8 points per concern    (range: 0 to -32)
  // 5. Vitamin D Factor    -> tiered by blood level     (range: -10 to +10, optional)
  // Final score is the sum of all factors, clamped to [0, 100].
  // Range check: worst case (~9) to best case (~100+, clamped), avoiding
  // an unrealistic 0 while still clearly signaling "Needs Improvement".
  // ============================================================
  const getScoreBreakdown = (): { label: string; points: number; detail: string }[] => {
    const factors: { label: string; points: number; detail: string }[] = []

    // Base score
    factors.push({
      label: 'Base Score',
      points: 40,
      detail: 'Neutral starting point for every assessment'
    })

    // 1. Energy level factor (3-30 points)
    factors.push({
      label: 'Energy Level',
      points: formData.energyLevel * 3,
      detail: `Self-reported energy of ${formData.energyLevel}/10`
    })

    // 2. Age factor (3-10 points)
    const age = parseInt(formData.age) || 0
    let agePoints = 3
    let ageDetail = 'Age 65+'
    if (age < 30) { agePoints = 10; ageDetail = 'Under 30' }
    else if (age < 50) { agePoints = 7; ageDetail = '30-49 years' }
    else if (age < 65) { agePoints = 5; ageDetail = '50-64 years' }
    factors.push({ label: 'Age Factor', points: agePoints, detail: ageDetail })

    // 3. Lifestyle factor (5-15 points)
    let lifestylePoints = 5
    let lifestyleDetail = 'Sedentary lifestyle'
    if (formData.lifestyle === 'athlete') { lifestylePoints = 15; lifestyleDetail = 'Athlete-level activity' }
    else if (formData.lifestyle === 'active') { lifestylePoints = 10; lifestyleDetail = 'Active lifestyle' }
    factors.push({ label: 'Lifestyle', points: lifestylePoints, detail: lifestyleDetail })

    // 4. Health concerns deduction (-8 per concern)
    if (formData.concerns.length > 0) {
      factors.push({
        label: 'Health Concerns',
        points: -8 * formData.concerns.length,
        detail: `${formData.concerns.length} concern(s) selected`
      })
    }

    // 5. Vitamin D factor (-10 to +10 points, optional)
    if (formData.vitaminDLevel) {
      const vitD = parseFloat(formData.vitaminDLevel)
      let vitDPoints = 0
      let vitDDetail = ''
      if (vitD >= 40) { vitDPoints = 10; vitDDetail = `${vitD} ng/mL (Optimal)` }
      else if (vitD >= 30) { vitDPoints = 5; vitDDetail = `${vitD} ng/mL (Normal)` }
      else if (vitD >= 20) { vitDPoints = -5; vitDDetail = `${vitD} ng/mL (Insufficient)` }
      else { vitDPoints = -10; vitDDetail = `${vitD} ng/mL (Deficient)` }
      factors.push({ label: 'Vitamin D Level', points: vitDPoints, detail: vitDDetail })
    }

    return factors
  }

  const scoreBreakdown = getScoreBreakdown()
  const vitalityScore = Math.max(0, Math.min(100, Math.round(scoreBreakdown.reduce((sum, f) => sum + f.points, 0))))

  // Shared derived values used by both recommendation rules and edge-case advisories
  const vitDLevel = formData.vitaminDLevel ? parseFloat(formData.vitaminDLevel) : null
  const isSevereVitaminDDeficiency = vitDLevel !== null && vitDLevel < 20
  const hasMultipleConcerns = formData.concerns.length >= 3

  // ============================================================
  // RECOMMENDATION RULES (documented, evaluated in priority order)
  // ============================================================
  // Priority 1: Immunity concern OR Vitamin D < 30 ng/mL       -> D Vital 10,000
  // Priority 2: Fatigue concern + energy level <= 5            -> B Vital Total
  // Priority 3: Athlete lifestyle                              -> B-Vital Plus Electrolytes
  // Priority 4: Brain fog concern (if B Vital Total not added) -> B Vital Total
  // Priority 5: Hair health concern                            -> Immunovit Plus
  // Priority 6 (edge case): Low energy (<=4) with no concerns  -> B Vital Total (general support)
  // Fallback: No rule matched                                  -> Immunovit Plus (general wellness)
  // Results are de-duplicated and capped at 3 products, kept in priority order.
  // ============================================================
  const getRecommendedProducts = (): Product[] => {
    const products: Product[] = []

    // Priority 1: Immunity concern OR low Vitamin D → D Vital 10,000
    if (formData.concerns.includes('immunity') || (vitDLevel !== null && vitDLevel < 30)) {
      products.push({
        id: 'd-vital-10000',
        name: 'D Vital 10,000',
        description: 'High-potency Vitamin D for immunity',
        icon: '☀️',
        rating: 4.9,
        benefits: [
          'Strengthens immune system',
          'Supports bone health',
          'Improves mood and mental health',
          'High bioavailability formula'
        ],
        affiliateUrl: '#',
        reason: vitDLevel !== null && vitDLevel < 30 
          ? `Your Vitamin D level (${vitDLevel} ng/mL) is below optimal range`
          : 'Recommended for immune system support'
      })
    }

    // Priority 2: Fatigue + Low/moderate energy → B Vital Total
    if (formData.concerns.includes('fatigue') && formData.energyLevel <= 5) {
      products.push({
        id: 'b-vital-total',
        name: 'B Vital Total',
        description: 'Complete B-Complex for energy and focus',
        icon: '💊',
        rating: 4.8,
        benefits: [
          'Boosts energy levels naturally',
          'Supports healthy metabolism',
          'Reduces fatigue and exhaustion',
          'Enhances mental clarity'
        ],
        affiliateUrl: '#',
        reason: 'Recommended for fatigue and low energy levels'
      })
    }

    // Priority 3: Athlete lifestyle → B-Vital Plus Electrolytes
    if (formData.lifestyle === 'athlete') {
      products.push({
        id: 'b-vital-electrolytes',
        name: 'B-Vital Plus Electrolytes',
        description: 'Advanced formula for athletes',
        icon: '⚡',
        rating: 4.7,
        benefits: [
          'Enhanced athletic performance',
          'Rapid hydration and recovery',
          'Maintains electrolyte balance',
          'Reduces muscle fatigue'
        ],
        affiliateUrl: '#',
        reason: 'Specially formulated for high-intensity training'
      })
    }

    // Priority 4: Brain fog → B Vital Total (if not already recommended)
    if (formData.concerns.includes('brain') && !products.find(p => p.id === 'b-vital-total')) {
      products.push({
        id: 'b-vital-total',
        name: 'B Vital Total',
        description: 'Complete B-Complex for energy and focus',
        icon: '💊',
        rating: 4.8,
        benefits: [
          'Enhances mental clarity',
          'Supports cognitive function',
          'Reduces brain fog',
          'Improves focus and concentration'
        ],
        affiliateUrl: '#',
        reason: 'Recommended for cognitive support and mental clarity'
      })
    }

    // Priority 5: Hair health → Immunovit Plus
    if (formData.concerns.includes('hair')) {
      products.push({
        id: 'immunovit-plus',
        name: 'Immunovit Plus',
        description: 'Complete multivitamin with biotin',
        icon: '🛡️',
        rating: 4.7,
        benefits: [
          'Promotes healthy hair growth',
          'Strengthens nails and skin',
          'Comprehensive nutrient support',
          'High-potency biotin formula'
        ],
        affiliateUrl: '#',
        reason: 'Recommended for hair, skin, and nail health'
      })
    }

    // Priority 6 (edge case): Low energy but no specific concern checked.
    // Ensures users who report low energy without checking "Fatigue" still
    // receive a relevant, conservative recommendation instead of a generic one.
    if (formData.energyLevel <= 4 && formData.concerns.length === 0 && !products.find(p => p.id === 'b-vital-total')) {
      products.push({
        id: 'b-vital-total',
        name: 'B Vital Total',
        description: 'Complete B-Complex for energy and focus',
        icon: '💊',
        rating: 4.8,
        benefits: [
          'Boosts energy levels naturally',
          'Supports healthy metabolism',
          'Reduces fatigue and exhaustion',
          'Enhances mental clarity'
        ],
        affiliateUrl: '#',
        reason: 'Recommended based on your low reported energy level'
      })
    }

    // Fallback: No rule matched → general wellness multivitamin
    if (products.length === 0) {
      products.push({
        id: 'immunovit-plus',
        name: 'Immunovit Plus',
        description: 'Advanced immune support formula',
        icon: '🛡️',
        rating: 4.7,
        benefits: [
          'Zinc + Vitamin C complex',
          'Antioxidant protection',
          'Daily defense formula',
          'Supports overall wellness'
        ],
        affiliateUrl: '#',
        reason: 'Recommended for daily nutritional support'
      })
    }

    // De-duplicate while preserving priority order, cap at 3 products
    const uniqueProducts = Array.from(new Map(products.map(p => [p.id, p])).values())
    return uniqueProducts.slice(0, 3)
  }

  const recommendedProducts = getRecommendedProducts()

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-teal-600'
    if (score >= 40) return 'text-orange-600'
    return 'text-red-600'
  }

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent'
    if (score >= 60) return 'Good'
    if (score >= 40) return 'Moderate'
    return 'Needs Improvement'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-orange-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
            <Award className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Your Personalized <span className="text-teal-600">Vitality Protocol</span>
          </h1>
          <p className="text-lg text-gray-600">
            Based on your responses, here's your customized vitamin plan
          </p>
        </div>

        {/* Vitality Score Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Vitality Score</h2>
            <div className="relative inline-flex items-center justify-center">
              <svg className="w-48 h-48 transform -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="#e5e7eb"
                  strokeWidth="12"
                  fill="none"
                />
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke={vitalityScore >= 80 ? '#10b981' : vitalityScore >= 60 ? '#14b8a6' : vitalityScore >= 40 ? '#f97316' : '#ef4444'}
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${vitalityScore * 5.53} 553`}
                  strokeLinecap="round"
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className={`text-6xl font-bold ${getScoreColor(vitalityScore)}`}>
                  {vitalityScore}
                </div>
                <div className="text-gray-600 text-lg">out of 100</div>
              </div>
            </div>
            <div className={`text-2xl font-semibold mt-6 ${getScoreColor(vitalityScore)}`}>
              {getScoreLabel(vitalityScore)}
            </div>
            <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
              {vitalityScore >= 80 && "Excellent! You're doing great. The recommendations below will help maintain your vitality."}
              {vitalityScore >= 60 && vitalityScore < 80 && "Good foundation! With the right supplements, you can boost your vitality further."}
              {vitalityScore >= 40 && vitalityScore < 60 && "There's room for improvement. Follow the recommendations to enhance your energy and wellness."}
              {vitalityScore < 40 && "Let's work on improving your vitality. The supplements below are specifically chosen for your needs."}
            </p>

            {/* Score Breakdown Toggle - Transparency Element (E-E-A-T) */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <button
                onClick={() => setShowBreakdown(!showBreakdown)}
                className="flex items-center justify-center mx-auto text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
              >
                <Info className="w-4 h-4 mr-1.5" />
                How is my score calculated?
                {showBreakdown ? <ChevronUp className="w-4 h-4 ml-1.5" /> : <ChevronDown className="w-4 h-4 ml-1.5" />}
              </button>
              {showBreakdown ? (
                <div className="mt-4 bg-gray-50 rounded-lg p-4 text-left max-w-md mx-auto">
                  {scoreBreakdown.map((factor, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{factor.label}</div>
                        <div className="text-xs text-gray-500">{factor.detail}</div>
                      </div>
                      <div className={`text-sm font-bold ${factor.points >= 0 ? 'text-teal-600' : 'text-red-500'}`}>
                        {factor.points >= 0 ? '+' : ''}{factor.points}
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-3 mt-1">
                    <div className="text-sm font-semibold text-gray-900">Final Score (clamped 0-100)</div>
                    <div className="text-sm font-bold text-gray-900">{vitalityScore}</div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Edge Case Advisories */}
        {(isSevereVitaminDDeficiency || hasMultipleConcerns) ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 flex items-start space-x-3">
            <Info className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700 space-y-1">
              {isSevereVitaminDDeficiency ? (
                <p><strong className="font-semibold">Low Vitamin D detected:</strong> Your reported level suggests a deficiency. We recommend seeing a doctor for personalized dosing guidance alongside the recommendation below.</p>
              ) : null}
              {hasMultipleConcerns ? (
                <p><strong className="font-semibold">Multiple concerns selected:</strong> With {formData.concerns.length} health concerns reported, a healthcare provider can help evaluate underlying causes in addition to the supplements suggested here.</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Recommended Products */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
            <TrendingUp className="w-8 h-8 text-teal-600 mr-3" />
            Recommended for You
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendedProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-xl border-2 border-teal-200 p-6 hover:shadow-lg transition-all">
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg h-32 mb-4 flex items-center justify-center">
                  <div className="text-5xl">{product.icon}</div>
                </div>
                
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                  <div className="flex items-center space-x-1">
                    <span className="text-yellow-500">★</span>
                    <span className="text-sm font-semibold text-gray-900">{product.rating}</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-3">{product.description}</p>

                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4">
                  <p className="text-xs font-medium text-teal-800">
                    <strong>Why recommended:</strong> {product.reason}
                  </p>
                </div>

                <ul className="space-y-2 mb-6">
                  {product.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={product.affiliateUrl}
                  className="flex items-center justify-center w-full py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                >
                  View Best Price
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Action Protocol */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Vitality Action Plan</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Start with the recommended supplements</h3>
                <p className="text-gray-600 text-sm">Begin with one or two products above that match your primary concerns.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Take consistently for 4-6 weeks</h3>
                <p className="text-gray-600 text-sm">Most vitamin benefits become noticeable after consistent use.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">3</div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Monitor your energy and well-being</h3>
                <p className="text-gray-600 text-sm">Track how you feel and adjust your protocol as needed.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <strong className="font-semibold">Medical Disclaimer:</strong> This calculator is for informational purposes and is not medical advice. The Vitality Score and recommendations are based on general guidelines and should not replace professional medical consultation. Always consult with a healthcare provider before starting any new supplements, especially if you have existing health conditions or take medications.
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={onReset}
            className="px-8 py-3 bg-gray-200 text-gray-900 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Retake Assessment
          </button>
          <button
            onClick={() => window.print()}
            className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Save Results (Print)
          </button>
        </div>
      </div>
    </div>
  )
}

export default CalculatorResults
